import { Ability } from '@casl/ability';
import {
  Actions,
  LocationForm,
  MultiSelectOption,
  ResourceBulkForm,
  ServiceInformation,
  Subjects,
} from '@dispatch/shared/types';
import { CreateNewEntityButton, TabPanel } from '@dispatch/web/ui';
import { Box, Grid, Toolbar } from '@mui/material';
import { useState } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  useFieldArray,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { ExclusionZonesInformation } from './ExclusionZonesInformation';
import { LocationInformationBulk } from './LocationInformationBulk';
import { LocationSidebarBulk } from './LocationSideBarBulk';
import { ServiceInformationBulk } from './ServiceInformationBulk';
import { red } from '@mui/material/colors';

export type LocationsProps = {
  control: Control<FieldValues>;
  permissions: Ability<[Actions, Subjects]> | undefined;
  watch: UseFormWatch<ResourceBulkForm>;
  errors: FieldErrors;
  statesOptions: MultiSelectOption[];
  resourceSetValue: UseFormSetValue<ResourceBulkForm>;
  resourceErrors: FieldErrors;
  trigger: UseFormTrigger<ResourceBulkForm>;
};

export function LocationsTabBulk({
  control,
  permissions,
  watch,
  errors,
  statesOptions,
  resourceSetValue,
  resourceErrors,
  trigger,
}: LocationsProps) {
  const { fields, append } = useFieldArray({
    control,
    name: 'locations',
  });
  const [currentLocation, setLocation] = useState(0);
  const [selected, setSelected] = useState(0);
  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');
  const emptyLocationForm: LocationForm = {
    locationInformation: {
      name: `New Location`,
      state: '',
      zip: '',
      county: '',
      isVirtualLocation: 'false',
    },
    serviceInformation: {
      startDate: new Date().toISOString(),
      endDate: '',
      allowedTravelRadius: 0,
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    } as ServiceInformation,
  };

  const selectLocation = (index: number) => {
    setLocation(index);
    setSelected(index);
  };

  const addLocation = () => {
    const newLocationIndex = fields.length;
    append(emptyLocationForm);
    selectLocation(newLocationIndex);
  };

  const onDeleteLocation = (locationIndex: number) => {
    const remove = watch('locations');
    remove.splice(locationIndex, 1);
    resourceSetValue('locations', remove);
  };

  const renderLocations = (currentTab: number) =>
    fields.map((field, index) => {
      return (
        <TabPanel value={currentTab} index={index} key={field.id}>
          <Grid container spacing={2}>
            <Grid container spacing={0} size={7}>
              <LocationInformationBulk
                control={control}
                index={index}
                permissions={permissions}
                errors={errors?.[index]}
                statesOptions={statesOptions}
                watch={watch}
                isReadOnly={false}
              />
            </Grid>
            <Grid container spacing={0} size={5}>
              <ServiceInformationBulk
                control={control}
                index={index}
                permissions={permissions}
                errors={errors?.[index]}
                watch={watch}
                resourceSetValue={resourceSetValue}
                trigger={trigger}
              />
            </Grid>
          </Grid>
          <Grid container size={12}>
            <ExclusionZonesInformation
              control={control}
              index={index}
              errors={errors}
              permissions={permissions}
            />
          </Grid>
        </TabPanel>
      );
    });

  return (
    <div
      style={{
        padding: '24px',
      }}
    >
      <Grid container spacing={2} alignItems="start">
        <Grid
          style={{
            border: resourceErrors?.['locations']
              ? `1px solid ${red[500]}`
              : undefined,
          }}
          size={3}
          sx={{
            mt: -2,
            ml: -2,
            pt: 2,
            pl: 2,
            width: 'calc((100% + 16px) * 3 / 12)',
          }}
        >
          <LocationSidebarBulk
            selected={selected}
            setSelected={setSelected}
            currentLocation={currentLocation}
            setLocation={setLocation}
            permissions={permissions}
            watch={watch}
            onDeleteLocation={onDeleteLocation}
            addLocation={addLocation}
          />
          <p
            style={{
              padding: 0,
              margin: 0,
              color: red[800],
            }}
          >
            {resourceErrors?.['locations']?.message || ''}
          </p>
        </Grid>
        <Grid container spacing={0} size={9}>
          {renderLocations(currentLocation)}
        </Grid>
      </Grid>
    </div>
  );
}
